import "./Clients.css";
import { Trans, useTranslation } from "react-i18next";
import { Content, Header, Main } from "../../commons/Template/Template";
import { Fragment, useEffect, useState } from "react";
import { Form, FormLoader } from "../../commons/Form/Form";
import api from "../../../api/resources";
import { Briefcase } from "react-feather";
import Button from "../../commons/Button/Button";
import {
  AccountBalance,
  Archive,
  DeleteForeverOutlined,
  NoAccounts,
  NotificationsActive,
  Visibility,
  Bolt,
  RocketLaunch,
  Close,
  ExpandMore,
  ExpandLess,
  UnfoldMore,
  Cancel,
  Search,
  Comment,
} from "@mui/icons-material";
import { Input } from "../../commons/Form/Form";
import EmptySection from "../../commons/EmptySection/EmptySection";
import { dashboardRoute } from "../../../routes/routes";
import { Link } from "react-router-dom";
import { useToastsDispatch } from "../../commons/Toasts/ToastsContext";
import { useModalDispatch } from "../../commons/Modal/ModalContext";
import Tabs from "../../commons/Tabs/Tabs";
import {
  Table,
  TableBody,
  TableHeader,
  TableRow,
  TableWrapper,
} from "../../commons/Table/Table";
import Pill from "../../Financing/Common/Pill/Pill";
import {
  SideDrawer,
  SideDrawerHeader,
} from "../../commons/SideDrawer/SideDrawer";
import {
  Indicator,
  IndicatorContainer,
  IndicatorIconWrapper,
  Indicators,
  IndicatorText,
} from "../../commons/Indicators/Indicators";
import { formatAmount, formatDate } from "../../../format";
import { i18n } from "../../../i18n";
import { useAuth } from "../../../auth/AuthContext";
import { NotificationsComponent } from "../../Notifications/Notifications";
import { useClients } from "./ClientsContext";
import { CompanyDisplayCard } from "../../Onboarding/Onboarding";

export default function Clients() {
  const { t } = useTranslation(["common", "reseller"]);
  document.title = t("reseller:clients.title");

  const { loading, tabs, selectedTab, onTabSelect } = useClients();

  return (
    <Main themeSwitch>
      <Header
        heading={t("reseller:clients.title")}
        subheading={t("reseller:clients.subheading")}
      />
      <Content className="Clients">
        <ClientSearchForm />
        <Tabs
          smallTabs
          tabs={tabs.map((tab) => ({
            id: tab.type,
            title: tab.title,
          }))}
          selectedID={selectedTab}
          onSelect={onTabSelect}
        />
        {loading ? <FormLoader /> : <ClientList />}
      </Content>
    </Main>
  );
}

function ClientList() {
  const { init, allClients, selectedTab: type, onClientsUpdate } = useClients();
  const { t } = useTranslation(["common", "reseller"]);
  const dispatchToast = useToastsDispatch();

  const [clientList, setClientList] = useState([]);

  // sorting
  const [sortConfig, setSortConfig] = useState({
    column: "name",
    direction: "asc",
  });

  const handleSort = (columnName) => {
    let direction = "asc";
    if (sortConfig.column === columnName && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({
      column: columnName,
      direction: direction,
    });
  };

  const sortFn = (a, b) => {
    if (a[sortConfig.column] < b[sortConfig.column]) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (a[sortConfig.column] > b[sortConfig.column]) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  };

  // filtering
  const filterByTab = (client) =>
    type === "managed" ? !client.archived : client.archived;
  const [filterTerm, setFilterTerm] = useState("");
  const [filterOptions, setFilterOptions] = useState(null);

  const filterByText = (client) =>
    client.name.toLowerCase().includes(filterTerm.toLowerCase()) ||
    client.code.includes(filterTerm) ||
    (client.reseller_note &&
      client.reseller_note.toLowerCase().includes(filterTerm.toLowerCase()));

  const filterByOptions = filterOptions ? filterOptions.filterFn : () => true;

  // init and update client list
  const updateClientList = () => {
    const sortedAndFilteredClients = allClients
      .filter(filterByTab)
      .filter(filterByText)
      .filter(filterByOptions)
      .sort(sortFn);

    setClientList(sortedAndFilteredClients);
  };

  useEffect(() => {
    updateClientList();
  }, [type, filterTerm, filterOptions, sortConfig, onClientsUpdate]);

  // clear filters when switching tabs
  const clearFilters = () => {
    setFilterOptions(null);
    setFilterTerm("");
  };
  useEffect(() => {
    clearFilters();
  }, [type]);

  // Drawer
  const [drawer, setDrawer] = useState(false);
  const toggleDrawer = (refresh = false) => {
    setDrawer((drawer) => !drawer);
    if (refresh) init();
  };

  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedDrawer, setSelectedDrawer] = useState("details");
  const handleSelectClient = (id, drawerOption = "details") => {
    setSelectedClient(clientList.find((client) => client.id === id));
    setSelectedDrawer(drawerOption);
    toggleDrawer();
  };

  const handleManage = async (clientID) => {
    const response = await api.manageClient(clientID);
    if (response.ok) {
      window.location.href = dashboardRoute; // not using useNavigate because needs page refresh
    } else {
      dispatchToast({
        type: "add",
        variant: "warning",
        heading: t("reseller:clients.failedManageToast"),
      });
    }
  };

  return (
    <div className="ClientList">
      <div className="ClientList-header">
        <div className="ClientList-header-text">
          <h3>Clients</h3>
          <small>{t(`reseller:clients.listHeaders.${type}`)}</small>
        </div>

        <ClientFilters
          type={type}
          onFilterFnChange={setFilterOptions}
          filterTerm={filterTerm}
          onFilterTermChange={setFilterTerm}
        />
      </div>
      <ClientTable
        clientList={clientList}
        onClientListChange={setClientList}
        type={type}
        onSelect={handleSelectClient}
        onSort={handleSort}
        sortConfig={sortConfig}
        onManage={handleManage}
      />

      <ClientDrawer
        drawer={drawer}
        toggleDrawer={toggleDrawer}
        selectedClient={selectedClient}
        selectedDrawer={selectedDrawer}
        type={type}
        onManage={handleManage}
      />
    </div>
  );
}

function ClientTable({
  clientList,
  type,
  onSelect,
  onSort,
  sortConfig,
  onManage,
}) {
  const { t } = useTranslation(["common", "reseller"]);

  const dispatchModal = useModalDispatch();
  const dispatchToast = useToastsDispatch();

  const { onTabSelect, init, onClientsUpdate } = useClients();

  const handleArchive = async (clientID) => {
    const nextArchivedState = !clientList.find(
      (client) => client.id === clientID,
    ).archived;
    const response = await api.updateClient(clientID, {
      id: clientID,
      archived: nextArchivedState,
    });
    if (response.ok) {
      onClientsUpdate(clientID, "archived", nextArchivedState); // used to be init();
      dispatchToast({
        type: "add",
        variant: "success",
        heading: nextArchivedState
          ? t("reseller:clients.archiveToast.heading")
          : t("reseller:clients.unarchiveToast.heading"),
      });

      const switchTabsWhenArchiving = false;
      switchTabsWhenArchiving &&
        onTabSelect(nextArchivedState ? "archived" : "managed");
    }
  };

  const handleNote = (clientID, reseller_note) => {
    const modal = {
      title: t("reseller:clients.noteModal.title"),
      message: t("reseller:clients.noteModal.message"),
      component: (
        <ClientNoteModal clientID={clientID} reseller_note={reseller_note} />
      ),
      onSuccess: () => init(),
    };
    dispatchModal({ type: "add", ...modal });
  };

  const handleInvitation = (clientID) => {
    const modal = {
      title: t("reseller:clients.inviteModal.title"),
      message: t("reseller:clients.inviteModal.message"),
      component: (
        <ClientInviteModal
          clientID={clientID}
          onInstantInviteSuccess={() => onTabSelect("managed")}
        />
      ),
      onSuccess: () => init(),
    };
    dispatchModal({ type: "add", ...modal });
  };

  const handleDelete = (clientID) => {
    const modal = {
      title: t("reseller:clients.deleteModal.title"),
      message: t("reseller:clients.deleteModal.message"),
      component: <ClientDeleteModal clientID={clientID} />,
      onSuccess: () => init(),
    };
    dispatchModal({ type: "add", ...modal });
  };

  const thStyle = {
    textTransform: "unset",
    whiteSpace: "normal",
    backgroundColor: "var(--color-leano-6)",
    height: "3rem",
  };

  const tdStyle = {
    minHeight: "3rem",
    height: "100%",
    boxSizing: "border-box",
    lineHeight: "1.5",
  };

  const [disabledButtons] = useState(true);

  return clientList && clientList.length ? (
    <TableWrapper>
      <Table
        nested
        fixedLayout
        style={{ position: "relative", minWidth: "65rem" }}
      >
        <TableHeader>
          <TableRow>
            <th style={{ ...thStyle }}>
              <SortingButton
                id={"name"}
                onSort={onSort}
                sortConfig={sortConfig}
              >
                {t("common:client")}
              </SortingButton>
            </th>

            {type === "managed" && (
              <Fragment>
                <th style={{ ...thStyle }}>
                  {" "}
                  {t("reseller:clients.tableHeaders.contact")}
                </th>
                <th style={{ ...thStyle }}>
                  <SortingButton
                    id={"pending_loans"}
                    onSort={onSort}
                    sortConfig={sortConfig}
                  >
                    {t("reseller:clients.tableHeaders.pendingLoans")}
                  </SortingButton>
                </th>
                <th style={{ ...thStyle, textAlign: "center" }}>
                  {t("reseller:clients.tableHeaders.bankingState")}
                </th>
                <th style={{ ...thStyle, textAlign: "center" }}>
                  {" "}
                  {t("reseller:clients.tableHeaders.events")}
                </th>
              </Fragment>
            )}

            <th style={{ ...thStyle, width: "15rem" }}></th>
          </TableRow>
        </TableHeader>
        <TableBody>
          {clientList.map((client) => (
            <TableRow key={client.id}>
              <td style={tdStyle}>
                <P>{client.name}</P>
                <P sub>{`SIREN ${client.code}`}</P>
              </td>

              {type === "managed" && (
                <Fragment>
                  <td style={tdStyle}>
                    {client.admin ? (
                      <Fragment>
                        <P>{client.admin.name}</P>
                        <P>{client.admin.email}</P>
                      </Fragment>
                    ) : client.invitation ? (
                      <Fragment>
                        <P>{client.invitation.email}</P>
                        <Button
                          text={t("reseller:clients.buttons.reinvite")}
                          variant={"link"}
                          style={{ color: "var(--color-leano-1)" }}
                          onClick={() => handleInvitation(client.id)}
                        />
                      </Fragment>
                    ) : (
                      <Fragment>
                        <P>{t("reseller:clients.placeholders.noContact")}</P>
                        <Button
                          text={t("reseller:clients.buttons.invite")}
                          style={{ color: "var(--color-leano-1)" }}
                          variant={"link"}
                          onClick={() => handleInvitation(client.id)}
                        />
                      </Fragment>
                    )}
                  </td>
                  <td
                    style={{ ...tdStyle, textAlign: " center" }}
                    title={client.pending_loans.length}
                  >
                    {client.pending_loans.length}
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <Pill
                      text={
                        client.banking_ok
                          ? t("reseller:clients.placeholders.synced")
                          : t("reseller:clients.placeholders.unsynced")
                      }
                      color={client.banking_ok ? "green" : "red"}
                    />
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      textAlign: "center",
                      whiteSpace: "normal",
                    }}
                  >
                    <NotificationsButton
                      unreadNb={client.updates_count}
                      onClick={() => onSelect(client.id, "notifications")}
                    />
                  </td>
                </Fragment>
              )}

              <td
                style={{
                  ...tdStyle,
                  display: "flex",
                  gap: "1.5rem",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {type === "managed" ? (
                  <Button
                    text={t("reseller:clients.buttons.manage")}
                    variant="primary_a"
                    {...(!client.admin
                      ? {
                          disabled: true,
                          title: t("reseller:clients.buttons.noManageTooltip"),
                        }
                      : {
                          onClick: () => onManage(client.id),
                        })}
                  />
                ) : (
                  <Button
                    text={t("reseller:clients.buttons.unarchive")}
                    variant="primary_a"
                    onClick={() => handleArchive(client.id)}
                  />
                )}
                <Button
                  icon={<Visibility />}
                  title={t("common:details")}
                  style={{ color: "var(--color-leano-1)" }}
                  onClick={() => onSelect(client.id, "details")}
                />

                {type === "managed" && !disabledButtons && (
                  <Button
                    icon={<Comment />}
                    title={t("reseller:clients.buttons.note")}
                    style={{ color: "var(--color-leano-1)" }}
                    onClick={() => handleNote(client.id, client.reseller_note)}
                  />
                )}

                {type === "managed" ? (
                  <Button
                    icon={<Archive />}
                    style={{ color: "var(--color-leano-1)" }}
                    onClick={() => handleArchive(client.id)}
                    title={t("reseller:clients.buttons.archive")}
                  />
                ) : (
                  <Button
                    icon={<DeleteForeverOutlined />}
                    style={{ color: "var(--color-leano-1)" }}
                    onClick={() => handleDelete(client.id)}
                    title={t("reseller:clients.buttons.delete")}
                  />
                )}
              </td>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableWrapper>
  ) : (
    <EmptySection
      icon={<NoAccounts />}
      text={t("reseller:clients.noClients")}
    />
  );
}

function NotificationsButton({ unreadNb, onClick }) {
  const { t } = useTranslation(["common", "reseller"]);

  const text = !unreadNb ? (
    t("reseller:clients.placeholders.events.new", { count: 0 })
  ) : (
    <Trans
      t={t}
      i18nKey={"reseller:clients.placeholders.events.new"}
      count={unreadNb}
      values={{ nb: unreadNb }}
    />
  );

  // if (unreadNb === 0) return <p>{text}</p>;
  return (
    <Link
      title={text}
      style={{
        color: "var(--color-leano-1)",
        width: "inherit",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
      onClick={onClick}
    >
      {text}
    </Link>
  );
}

function ClientDrawer({
  drawer,
  toggleDrawer,
  selectedClient,
  selectedDrawer = "details",
  onManage,
  type,
}) {
  const { t } = useTranslation(["common", "reseller"]);

  const handleCloseNotifications = async () => {
    if (selectedClient.updates_count) {
      await api.readAllClientNotifications(selectedClient.id);
      toggleDrawer(true);
    } else {
      toggleDrawer();
    }
  };

  const handleClose = () => {
    if (selectedDrawer === "details") {
      toggleDrawer();
    } else if (selectedDrawer === "notifications") {
      handleCloseNotifications();
    }
  };

  if (!selectedClient) return <></>;
  return (
    <SideDrawer
      className={"ClientDrawer"}
      anchor={"right"}
      open={drawer}
      onClose={handleClose}
    >
      {selectedDrawer === "details" ? (
        <div>
          <ClientDetails
            toggleDrawer={handleClose}
            selectedClient={selectedClient}
          />
          {type === "managed" && selectedClient.admin && (
            <Button
              style={{
                position: "absolute",
                bottom: "1rem",
                right: "1.5rem",
                left: "1.5rem",
              }}
              text={t("reseller:clients.buttons.manage")}
              variant="primary_a"
              onClick={() => onManage(selectedClient.id)}
            />
          )}
        </div>
      ) : selectedDrawer === "notifications" ? (
        <Fragment>
          <SideDrawerHeader
            title={
              !selectedClient.updates_count
                ? t("reseller:clients.placeholders.events.new", { count: 0 })
                : t("reseller:clients.tableHeaders.events")
            }
            onClose={handleClose}
          />
          <NotificationsComponent companyID={selectedClient.id} />
        </Fragment>
      ) : (
        <></>
      )}
    </SideDrawer>
  );
}

function ClientDetails({ toggleDrawer, selectedClient }) {
  const { t } = useTranslation(["common", "reseller"]);
  const { activeCompany, loadingActiveCompany } = useAuth();

  if (loadingActiveCompany) return <></>;
  return (
    <div className="ClientDrawer-details">
      <Button
        icon={<Close />}
        onClick={toggleDrawer}
        className="ClientDrawer-details-closeButton"
      />
      <Pill className="ClientDrawer-details-code" text={selectedClient.code} />
      <p className="ClientDrawer-details-name">{selectedClient.name}</p>
      {selectedClient.admin && (
        <div className="ClientDrawer-details-admin">
          <span>{selectedClient.admin?.name}</span>
          <span>{selectedClient.admin?.telephone}</span>
        </div>
      )}
      <Indicators className="ClientDetailsIndicators">
        <Indicator>
          <IndicatorIconWrapper
            className={selectedClient.banking_ok ? "success" : "warning"}
          >
            <AccountBalance />
          </IndicatorIconWrapper>
          <IndicatorContainer>
            <IndicatorText bold>
              {t("reseller:clients.indicators.bankState")}
            </IndicatorText>
            <IndicatorText>
              <Trans
                t={t}
                i18nKey={"reseller:clients.indicators.treasury"}
                values={{
                  amount: formatAmount(
                    selectedClient.bank_info.balance,
                    i18n.resolvedLanguage,
                    activeCompany.currency,
                  ),
                }}
              />
            </IndicatorText>
            {selectedClient.bank_info.update_date && (
              <IndicatorText>
                <Trans
                  t={t}
                  i18nKey={"reseller:clients.indicators.lastUpdate"}
                  values={{
                    date: formatDate(
                      selectedClient.bank_info.update_date,
                      i18n.resolvedLanguage,
                    ),
                  }}
                />
              </IndicatorText>
            )}
          </IndicatorContainer>
        </Indicator>

        <Indicator>
          <IndicatorIconWrapper
            className={selectedClient.alerts ? "error" : "success"}
          >
            <NotificationsActive />
          </IndicatorIconWrapper>
          <IndicatorContainer>
            <IndicatorText bold>
              <Trans
                t={t}
                i18nKey={"reseller:clients.indicators.alerts"}
                count={selectedClient.alerts}
                values={{
                  nb: selectedClient.alerts,
                }}
              />
            </IndicatorText>
            <IndicatorText>
              {t("reseller:clients.indicators.last6Months")}
            </IndicatorText>
          </IndicatorContainer>
        </Indicator>

        <Indicator>
          <IndicatorIconWrapper>
            <RocketLaunch />
          </IndicatorIconWrapper>
          <IndicatorContainer>
            <IndicatorText bold>
              <Trans
                t={t}
                i18nKey={"reseller:clients.indicators.loans"}
                count={selectedClient.accepted_loans.count}
                values={{
                  nb: selectedClient.accepted_loans.count,
                }}
              />
            </IndicatorText>
            {selectedClient.accepted_loans.count > 0 && (
              <>
                <IndicatorText>
                  <Trans
                    t={t}
                    i18nKey={"reseller:clients.indicators.amount"}
                    values={{
                      amount: formatAmount(
                        selectedClient.accepted_loans.amount,
                        i18n.resolvedLanguage,
                        activeCompany.currency,
                      ),
                    }}
                  />
                </IndicatorText>
                <IndicatorText>
                  <Trans
                    t={t}
                    i18nKey={"reseller:clients.indicators.lastLoanDate"}
                    values={{
                      date: formatDate(
                        selectedClient.accepted_loans.last_date,
                        i18n.resolvedLanguage,
                      ),
                    }}
                  />
                </IndicatorText>
              </>
            )}
          </IndicatorContainer>
        </Indicator>

        <Indicator>
          <IndicatorIconWrapper>
            <Bolt />
          </IndicatorIconWrapper>
          <IndicatorContainer>
            <IndicatorText bold>
              <Trans
                t={t}
                i18nKey={"reseller:clients.indicators.requests"}
                count={selectedClient.pending_loans.length}
                values={{
                  nb: selectedClient.pending_loans.length,
                }}
              />
            </IndicatorText>
            {selectedClient.pending_loans.length > 0 && (
              <IndicatorText div>
                <ul style={{ margin: "0" }}>
                  {Object.entries(
                    selectedClient.pending_loans.reduce(
                      (accumulator, currentProvider) => ({
                        ...accumulator,
                        [currentProvider]: accumulator[currentProvider]
                          ? accumulator[currentProvider] + 1
                          : 1,
                      }),
                      {},
                    ),
                  ).map(([key, value]) => (
                    <li key={key}>
                      {key}
                      {value > 1 ? ` (${value})` : ""}
                    </li>
                  ))}
                </ul>
              </IndicatorText>
            )}
          </IndicatorContainer>
        </Indicator>
      </Indicators>
    </div>
  );
}

function ClientFilters({
  type,
  onFilterFnChange,
  filterTerm,
  onFilterTermChange,
}) {
  return (
    <div
      className={["ClientList-filters", type === "archived" && "textOnly"]
        .filter(Boolean)
        .join(" ")}
    >
      {type === "managed" && (
        <ClientFilterSelect onFilterFnChange={onFilterFnChange} />
      )}
      <ClientFilterText
        filterTerm={filterTerm}
        onFilterTermChange={onFilterTermChange}
      />
    </div>
  );
}

function P({ children, sub = false }) {
  return (
    <p
      title={children}
      style={sub ? { color: "var(--color-neutral-1)" } : null}
    >
      {children}
    </p>
  );
}

function SortingButton({ id, children, onSort, sortConfig }) {
  return (
    <Button
      onClick={() => onSort(id)}
      style={{
        fontSize: "inherit",
        fontWeight: "inherit",
        display: "flex",
        gap: "0.5rem",
        alignItems: "center",
        textAlign: "left",
        lineHeight: "inherit",
      }}
    >
      {children}
      {sortConfig.column === id ? (
        sortConfig.direction === "asc" ? (
          <ExpandMore />
        ) : (
          <ExpandLess />
        )
      ) : (
        <UnfoldMore />
      )}
    </Button>
  );
}

function ClientFilterSelect({ onFilterFnChange }) {
  const { t } = useTranslation(["common", "reseller"]);
  const [filters, setFilters] = useState(null);

  const handleChange = (value) => {
    setFilters(value);
    onFilterFnChange(value);
  };

  const options = [
    {
      value: "invite",
      filterFn: (client) => !client.invitation && !client.admin,
    },
    {
      value: "event",
      filterFn: (client) => client.updates_count,
    },
    {
      value: "bank",
      filterFn: (client) => !client.banking_ok,
    },
  ];

  return (
    <Input
      type={"select"}
      isClearable
      placeholder={t("common:all")}
      options={options.map((option) => ({
        ...option,
        label: t(`reseller:clients.filters.${option.value}`),
      }))}
      value={filters}
      onChange={handleChange}
      className="ClientList-filters-select"
    />
  );
}

function ClientSearchForm() {
  const { t } = useTranslation(["common", "reseller"]);

  const { init } = useClients();

  const dispatchModal = useModalDispatch();
  const dispatchToast = useToastsDispatch();

  const [searchKey, setSearchKey] = useState("");
  const [searchSuccess, setSearchSuccess] = useState(false);

  const handleSearch = async () => {
    const response = await api.searchCompanies({
      key: searchKey.replace(/\s+/g, ""),
    });
    if (response.ok) {
      setSearchSuccess(true);
      const companies = await response.json();
      if (companies.length === 0) {
        dispatchToast({
          type: "add",
          variant: "warning",
          heading: t("reseller:clients.emptySearchToast.heading"),
        });
      } else if (companies.length === 1) {
        const clientAddModal = {
          title: t("reseller:clients.addModal.title"),
          component: <ClientAddModal client={companies[0]} />,
          onSuccess: () => {
            setSearchKey("");
            init();
          },
        };
        dispatchModal({ type: "add", ...clientAddModal });
      }
    }
  };

  const inputs = [
    {
      type: "text",
      placeholder: t("reseller:clients.searchForm.placeholder"),
      name: "key",
      pattern: "[0-9\\s]+",
      value: searchKey,
      onChange: setSearchKey,
    },
  ];

  const form = {
    inputs: [{ data: inputs }],
    onSubmit: handleSearch,
    resetLoader: searchSuccess,
    variant: "compact",
    button: t("common:add"),
    className: "ClientSearch-Form",
  };

  return (
    <div className="ClientSearch">
      <div className="ClientSearch-label">
        <div className="ClientSearch-label-icon">
          <Briefcase />
        </div>
        <div className="ClientSearch-label-text">
          <strong>{t("reseller:clients.searchForm.heading")}</strong>
          <small>{t("reseller:clients.searchForm.subheading")}</small>
        </div>
      </div>
      <Form {...form} />
    </div>
  );
}

function ClientAddModal({ client }) {
  const { t } = useTranslation(["common", "reseller"]);
  const dispatchModal = useModalDispatch();
  const dispatchToast = useToastsDispatch();

  const handleAddClient = async () => {
    const response = await api.createClient(client);
    if (response.ok) {
      dispatchModal({ type: "success" });
      dispatchToast({
        type: "add",
        variant: "success",
        heading: t("reseller:clients.addSuccessToast.heading"),
      });
    } else {
      const errors = await response.json();
      dispatchModal({ type: "success" });
      dispatchToast({
        type: "add",
        variant: "warning",
        heading: t("reseller:clients.addFailToast.heading"),
        subheading: errors.code[0],
      });
    }
  };

  return (
    <Form onSubmit={handleAddClient} style={{ gap: "1.5rem" }}>
      <CompanyDisplayCard localCompany={client} />
    </Form>
  );
}

function ClientFilterText({ filterTerm, onFilterTermChange }) {
  const { t } = useTranslation(["common", "reseller"]);
  const textInput = {
    type: "text",
    placeholder: t("reseller:clients.filters.text"),
    value: filterTerm,
    onChange: onFilterTermChange,
    unstyled: true,
  };

  return (
    <div className="ClientList-filters-text">
      <Search className="ClientList-filters-text-search" />
      <Input {...textInput} />
      {filterTerm && (
        <Button
          className="ClientList-filters-text-clear"
          icon={<Cancel />}
          onClick={() => onFilterTermChange("")}
        />
      )}
    </div>
  );
}

function ClientNoteModal({ clientID, reseller_note = "" }) {
  const { t } = useTranslation(["common", "reseller"]);
  const dispatchModal = useModalDispatch();
  const [note, setNote] = useState(reseller_note);

  const handleSubmit = async () => {
    const response = await api.updateClient(clientID, {
      id: clientID,
      reseller_note: note,
    });
    if (response.ok) {
      dispatchModal({ type: "success" });
    }
  };

  return (
    <div
      className="ClientNoteModal"
      style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
    >
      <textarea
        maxLength={150}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <Button
        // disabled={!note}
        variant={"primary_a"}
        text={t("reseller:clients.noteModal.button")}
        onClick={handleSubmit}
      />
    </div>
  );
}

function ClientInviteModal({ clientID, onInstantInviteSuccess }) {
  const { t } = useTranslation(["common", "reseller"]);
  const dispatchModal = useModalDispatch();
  const dispatchToast = useToastsDispatch();

  const [invitation, setInvitation] = useState({
    company: clientID,
    first_name: "",
    surname: "",
    email: "",
    telephone: "",
  });

  const initialInvitationErrors = {
    company: [],
    first_name: [],
    surname: [],
    email: [],
    telephone: [],
    non_field_errors: [],
  };

  const [invitationErrors, setInvitationErrors] = useState(
    initialInvitationErrors,
  );

  const clearErrors = () => {
    setInvitationErrors(initialInvitationErrors);
  };

  const handleInvite = async () => {
    clearErrors();
    const response = await api.inviteClient(invitation);

    const pendingConfirmationToast = {
      variant: "info",
      heading: t(
        "reseller:clients.inviteModal.toasts.pendingConfirmation.heading",
      ),
      subheading: t(
        "reseller:clients.inviteModal.toasts.pendingConfirmation.subheading",
      ),
    };

    const instantAddToast = {
      variant: "success",
      heading: t("reseller:clients.inviteModal.toasts.instantAdd.heading"),
      subheading: t(
        "reseller:clients.inviteModal.toasts.instantAdd.subheading",
      ),
    };

    if (response.ok) {
      switch (response.status) {
        case 200:
          dispatchModal({ type: "success" });
          dispatchToast({
            type: "add",
            ...instantAddToast,
          });
          onInstantInviteSuccess();
          break;
        case 201:
          dispatchModal({ type: "success" });
          dispatchToast({
            type: "add",
            ...pendingConfirmationToast,
          });
          break;
        default: {
          dispatchModal({ type: "success" });
          break;
        }
      }
    } else {
      const jsonErrors = await response.json();
      setInvitationErrors(jsonErrors);
    }
  };

  const handleInvitationChange = (newValue, field) => {
    setInvitation((prevInvitation) => ({
      ...prevInvitation,
      [field]: newValue,
    }));
  };

  const inputs = [
    {
      type: "text",
      label: t("common:first_name"),
      name: "first_name",
      value: invitation.first_name,
      onChange: (value) => handleInvitationChange(value, "first_name"),
      errors: invitationErrors.first_name,
    },
    {
      type: "text",
      label: t("common:surname"),
      name: "surname",
      value: invitation.surname,
      onChange: (value) => handleInvitationChange(value, "surname"),
      errors: invitationErrors.surname,
    },
    {
      type: "tel",
      label: t("common:telephone"),
      name: "telephone",
      value: invitation.telephone,
      onChange: (value) => handleInvitationChange(value, "telephone"),
      errors: invitationErrors.telephone,
    },
    {
      type: "email",
      label: t("reseller:clients.inviteModal.label"),
      name: "email",
      value: invitation.email,
      onChange: (value) => handleInvitationChange(value, "email"),
      errors: invitationErrors.email,
    },
  ];

  const form = {
    errors: invitation.non_field_errors,
    inputs: [{ data: inputs }],
    onSubmit: handleInvite,
    button: t("reseller:clients.inviteModal.button"),
  };

  return <Form {...form} />;
}

function ClientDeleteModal({ clientID }) {
  const { t } = useTranslation(["common", "reseller"]);
  const dispatchModal = useModalDispatch();

  const handleSubmit = async () => {
    const response = await api.deleteClient(clientID);
    if (response.ok) {
      dispatchModal({ type: "success" });
    }
  };
  const form = {
    onSubmit: handleSubmit,
    button: t("reseller:clients.deleteModal.button"),
  };
  return <Form {...form} />;
}
